// A write-to-disk sink that degrades gracefully across browsers.
//
// Order of preference:
//   1. File System Access API (`showSaveFilePicker` + `createWritable`) — true
//      streaming to a user-chosen file; constant memory. Chrome/Edge desktop.
//   2. StreamSaver — pipes through a service-worker so the browser's own
//      download manager streams to disk. Works where FSA is missing but SW +
//      secure context exist.
//   3. In-memory Blob — buffers everything then triggers an anchor-click save.
//      The iOS Safari path; OOM risk on long movies (documented limitation).
//
// The capability lookups are injected via `FileSinkEnv` so the selection logic
// is unit-testable without a real browser (headless E2E also relies on the
// blob fallback, which is what CI asserts on).

export type SinkKind = 'fsa' | 'streamsaver' | 'blob';

export interface FileSink {
  kind: SinkKind;
  write(chunk: Uint8Array): Promise<void>;
  close(): Promise<void>;
  abort(): Promise<void>;
}

interface FsaWritable {
  write(chunk: Uint8Array): Promise<void>;
  close(): Promise<void>;
  abort?(): Promise<void>;
}
interface FsaHandle { createWritable(): Promise<FsaWritable>; }

interface StreamWriter {
  write(chunk: Uint8Array): Promise<void>;
  close(): Promise<void>;
  abort(reason?: unknown): Promise<void>;
}
interface StreamSaverLike {
  createWriteStream(filename: string, opts?: { size?: number }): { getWriter(): StreamWriter };
}

export interface FileSinkEnv {
  /** FSA picker — defaults to `window.showSaveFilePicker` when available. */
  showSaveFilePicker?: (opts: {
    suggestedName?: string;
    types?: { description?: string; accept: Record<string, string[]> }[];
  }) => Promise<FsaHandle>;
  /** Lazily resolve a StreamSaver instance, or null when unsupported. */
  loadStreamSaver?: () => Promise<StreamSaverLike | null>;
  /** Trigger a blob download — defaults to an anchor-click. */
  saveBlob?: (blob: Blob, filename: string) => void;
}

function defaultSaveBlob(blob: Blob, filename: string): void {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  // Revoke on the next tick so the navigation/download has started.
  setTimeout(() => URL.revokeObjectURL(url), 10_000);
}

/**
 * iOS / iPadOS detection. StreamSaver's service-worker download trick does not
 * work on iOS Safari (no Blob-stream download), so those clients must use the
 * in-memory Blob sink instead. iPadOS 13+ reports as desktop "Macintosh", so we
 * also treat a touch-capable Mac as iOS-like.
 */
export function isIosLike(ua: string, platform: string, maxTouchPoints: number): boolean {
  if (/iPad|iPhone|iPod/.test(ua) || /iPad|iPhone|iPod/.test(platform)) return true;
  // iPadOS masquerading as macOS: Mac UA/platform + a touch screen.
  if (/Mac/.test(platform) && maxTouchPoints > 1) return true;
  return false;
}

async function defaultLoadStreamSaver(): Promise<StreamSaverLike | null> {
  if (typeof window === 'undefined') return null;
  if (typeof navigator === 'undefined' || !('serviceWorker' in navigator)) return null;
  if (!window.isSecureContext) return null;
  // iOS can't stream-to-disk via a service worker → let the Blob tier handle it.
  if (isIosLike(navigator.userAgent, navigator.platform, navigator.maxTouchPoints ?? 0)) return null;
  try {
    const mod = await import('streamsaver');
    const streamSaver = (mod.default ?? mod) as unknown as StreamSaverLike & { mitm?: string };
    // Point StreamSaver at our SELF-HOSTED MITM + service worker (vendored in
    // public/streamsaver/) instead of the library's third-party GitHub Pages
    // default — removes a fragile cross-origin dependency that can be down,
    // blocked by adblockers, or refused by a strict CSP.
    try { streamSaver.mitm = `${window.location.origin}/streamsaver/mitm.html`; } catch { /* readonly? keep default */ }
    return streamSaver;
  } catch {
    return null;
  }
}

function defaultEnv(): Required<Pick<FileSinkEnv, 'loadStreamSaver' | 'saveBlob'>> & FileSinkEnv {
  const picker =
    typeof window !== 'undefined' && typeof (window as unknown as { showSaveFilePicker?: unknown }).showSaveFilePicker === 'function'
      ? (window as unknown as { showSaveFilePicker: FileSinkEnv['showSaveFilePicker'] }).showSaveFilePicker
      : undefined;
  return {
    ...(picker ? { showSaveFilePicker: picker } : {}),
    loadStreamSaver: defaultLoadStreamSaver,
    saveBlob: defaultSaveBlob,
  };
}

export async function createFileSink(
  filename: string,
  mime: string,
  env: FileSinkEnv = defaultEnv(),
  sizeHint?: number,
): Promise<FileSink> {
  // 1. File System Access API
  if (env.showSaveFilePicker) {
    try {
      const handle = await env.showSaveFilePicker({
        suggestedName: filename,
        types: [{ description: 'Video', accept: { [mime]: ['.mp4'] } }],
      });
      const writable = await handle.createWritable();
      return {
        kind: 'fsa',
        write: (chunk) => writable.write(chunk),
        close: () => writable.close(),
        abort: async () => { try { await writable.abort?.(); } catch { /* best-effort */ } },
      };
    } catch (err) {
      // User dismissed the native save dialog → propagate as a cancel so the
      // caller shows "canceled", not "failed". Any other FSA failure → fall
      // through to the next tier so the download still succeeds.
      if (err instanceof Error && err.name === 'AbortError') throw err;
    }
  }

  // 3. In-memory Blob — the universal fallback (works in every browser; only
  // downside is memory use on very large files, hence it's last).
  const makeBlobSink = (): FileSink => {
    let chunks: Uint8Array[] = [];
    let aborted = false;
    const saveBlob = env.saveBlob ?? defaultSaveBlob;
    return {
      kind: 'blob',
      write: async (chunk) => { if (!aborted) chunks.push(chunk); },
      close: async () => {
        if (aborted) return;
        const blob = new Blob(chunks as BlobPart[], { type: mime });
        chunks = [];
        saveBlob(blob, filename);
      },
      abort: async () => { aborted = true; chunks = []; },
    };
  };

  // 2. StreamSaver — streams to disk on FSA-less browsers (Firefox/Safari/
  //    Android) without buffering the whole file. If it can't initialize (SW
  //    registration refused, MITM unreachable, …) cascade to the Blob sink so
  //    the user still gets their download instead of a hard error.
  const streamSaver = env.loadStreamSaver ? await env.loadStreamSaver() : null;
  if (streamSaver) {
    try {
      const stream = streamSaver.createWriteStream(
        filename,
        sizeHint != null ? { size: sizeHint } : {},
      );
      const writer = stream.getWriter();
      return {
        kind: 'streamsaver',
        write: (chunk) => writer.write(chunk),
        close: () => writer.close(),
        abort: async () => { try { await writer.abort(); } catch { /* best-effort */ } },
      };
    } catch {
      return makeBlobSink();
    }
  }

  return makeBlobSink();
}
